// Maps the backend's fine-grained sandbox lifecycle (14 statuses) onto the
// designer's 5-node pipeline (Vend → Plan → Apply → DNS → Ready). Kept
// status-string based (no import from SandboxPage) so the design module has no
// dependency cycle with the pages.
import { color } from './tokens';

export type StageKey = 'vend' | 'plan' | 'apply' | 'dns' | 'ready';

export const STAGES: Array<{ key: StageKey; label: string }> = [
  { key: 'vend', label: 'Vend' },
  { key: 'plan', label: 'Plan' },
  { key: 'apply', label: 'Apply' },
  { key: 'dns', label: 'DNS' },
  { key: 'ready', label: 'Ready' },
];

// Per-stage visual status used by both the row segments and the overview
// stepper.
export type StageStatus = 'done' | 'running' | 'error' | 'pending';

export type PipelineInfo = {
  // Index of the stage the sandbox is currently at (0-4).
  index: number;
  running: boolean;
  destroying: boolean;
  failed: boolean;
  ready: boolean;
  // Coarse accent colour for the running/current node (amber while applying,
  // blue while destroying, green when ready, red when failed).
  color: string;
};

const RUNNING_AT: Record<string, number> = {
  planning_account: 0,
  creating_account: 0,
  bootstrapping_account: 0,
  preparing_state: 1,
  planning: 1,
  applying: 2,
  awaiting_dns: 3,
};

// Statuses that sit "between" running stages — the previous stage is done and
// the next hasn't started. We park the marker on the next stage as pending.
const IDLE_AT: Record<string, number> = {
  requested: 0,
  account_planned: 1,
  account_created: 1,
  planned: 2,
};

export const pipelineFor = (
  status: string,
  failedStageIndex?: number,
): PipelineInfo => {
  if (status === 'ready') {
    return { index: 4, running: false, destroying: false, failed: false, ready: true, color: color.green };
  }
  if (status === 'destroyed') {
    return { index: 4, running: false, destroying: false, failed: false, ready: false, color: color.textDimmer };
  }
  if (status === 'destroying') {
    return { index: 3, running: true, destroying: true, failed: false, ready: false, color: color.blue };
  }
  if (status === 'failed') {
    return {
      index: failedStageIndex ?? 2,
      running: false,
      destroying: false,
      failed: true,
      ready: false,
      color: color.red,
    };
  }
  if (status in RUNNING_AT) {
    return { index: RUNNING_AT[status], running: true, destroying: false, failed: false, ready: false, color: color.amber };
  }
  return { index: IDLE_AT[status] ?? 0, running: false, destroying: false, failed: false, ready: false, color: color.amber };
};

// Per-stage status for stage `i`, given the overall pipeline info.
export const stageStatusAt = (info: PipelineInfo, i: number): StageStatus => {
  if (info.ready) return 'done';
  if (info.destroying) {
    // Everything up to and including the current node reads as active/blue.
    return i <= info.index ? 'done' : 'pending';
  }
  if (info.failed) {
    if (i < info.index) return 'done';
    if (i === info.index) return 'error';
    return 'pending';
  }
  if (i < info.index) return 'done';
  if (i === info.index) return info.running ? 'running' : 'pending';
  return 'pending';
};

// Short "apply · 3/5" style caption shown under the row segments.
export const pipelineCaption = (status: string, info: PipelineInfo): string => {
  if (info.ready) return 'ready · 5/5';
  if (status === 'destroyed') return 'destroyed';
  if (info.destroying) return `destroy · ${info.index + 1}/5`;
  if (info.failed) return `failed at ${STAGES[info.index].label.toLowerCase()} · ${info.index + 1}/5`;
  return `${STAGES[info.index].label.toLowerCase()} · ${info.index + 1}/5`;
};
